import { getRequest, postRequest } from 'apis/ApiInstence';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const AddProject = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [packages, setPackages] = useState([]); // list of available packages
  const [loadingPackages, setLoadingPackages] = useState(false);

  const [formData, setFormData] = useState({
    project_location: '',
    project_area: '',
    project_floor: '',
    project_type: 'Residential',
    project_description: '',
    package_id: '',
    images: [],
  });

  // Fetch packages when project_type changes
  useEffect(() => {
    const fetchPackages = async () => {
      setLoadingPackages(true);
      try {
        const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
        const res = await getRequest(`package?property_type=${formData.project_type}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data.success) {
          setPackages(res.data.data);
          // Reset package selection if no longer valid
          if (formData.package_id && !res.data.data.some(p => p.id === parseInt(formData.package_id))) {
            setFormData(prev => ({ ...prev, package_id: '' }));
          }
        } else {
          toast.error(res.data.message || 'Failed to load packages');
        }
      } catch (error) {
        console.error('Error fetching packages:', error);
        toast.error('Failed to load packages');
      } finally {
        setLoadingPackages(false);
      }
    };
    fetchPackages();
  }, [formData.project_type]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    if (type === 'file' && name === 'images') {
      const imageFiles = Array.from(files);
      setFormData(prev => ({ ...prev, images: imageFiles }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!formData.project_location || !formData.project_area || !formData.project_type || !formData.project_description) {
      toast.error('Please fill all required fields');
      setLoading(false);
      return;
    }
    /* Removed mandatory package check */
    if (formData.images.length === 0) {
      toast.error('Please select at least one project image');
      setLoading(false);
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append('project_location', formData.project_location);
    formDataToSend.append('project_area', formData.project_area);
    formDataToSend.append('project_floor', formData.project_floor);
    formDataToSend.append('project_type', formData.project_type);
    formDataToSend.append('project_description', formData.project_description);
    formDataToSend.append('package_id', formData.package_id);
    formData.images.forEach(image => formDataToSend.append('images', image));

    try {
      const token = localStorage.getItem('userToken') || sessionStorage.getItem('userToken');
      if (!token) {
        toast.error('Authentication token missing. Please login again.');
        navigate('/signin');
        return;
      }
      await postRequest('projects', formDataToSend, {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'multipart/form-data',
      });
      toast.success('Project added successfully!');
      navigate('/projects');
    } catch (error) {
      console.error('Error adding project:', error);
      toast.error(error.response?.data?.message || 'Failed to add project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="mainheadig mx-4">
        <h4 className="font-weight-bold">Create New Project</h4>
        <p className="text-muted" style={{marginTop: '-1.5rem', marginBottom: '2rem'}}>Fill in the details below to add a new project to your portfolio.</p>
      </div>

      <form onSubmit={handleSubmit} className="form-style mx-4">
        <div className="row">
          <div className="col-12 mb-4">
            <h5 className="font-weight-bold text-primary" style={{fontSize: '1.1rem', borderLeft: '4px solid var(--primary)', paddingLeft: '1rem'}}>General Information</h5>
          </div>
          
          <div className="col-md-4 mb-4">
            <label htmlFor="projectLocation" className="lables">Project Location *</label>
            <input
              type="text"
              id="projectLocation"
              name="project_location"
              className="form-control"
              value={formData.project_location}
              onChange={handleChange}
              placeholder="Enter city or area"
              required
            />
          </div>

          <div className="col-md-4 mb-4">
            <label htmlFor="projectArea" className="lables">Project Area (sq ft) *</label>
            <input
              type="number"
              id="projectArea"
              name="project_area"
              className="form-control"
              value={formData.project_area}
              onChange={handleChange}
              placeholder="e.g. 1200"
              required
            />
          </div>

          <div className="col-md-4 mb-4">
            <label htmlFor="projectFloor" className="lables">Project Floor</label>
            <input
              type="text"
              id="projectFloor"
              name="project_floor"
              className="form-control"
              value={formData.project_floor}
              onChange={handleChange}
              placeholder="e.g., G+2, 5th Floor"
            />
          </div>

          <div className="col-md-4 mb-4">
            <label htmlFor="projectType" className="lables">Project Type *</label>
            <select
              id="projectType"
              name="project_type"
              className="form-control"
              value={formData.project_type}
              onChange={handleChange}
              required
            >
              <option value="Residential">Residential</option>
              <option value="Commercial">Commercial</option>
              <option value="Industrial">Industrial</option>
              <option value="Townships">Townships</option>
              <option value="Farmhouses">Farmhouses</option>
            </select>
          </div>

          <div className="col-md-4 mb-4">
            <label htmlFor="packageId" className="lables">Select Package</label>
            <select
              id="packageId"
              name="package_id"
              className="form-control"
              value={formData.package_id}
              onChange={handleChange}
              disabled={loadingPackages}
            >
              <option value="">-- Choose a package --</option>
              {packages.map(pkg => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name} (₹{pkg.price_sqft}/sq.ft)
                </option>
              ))}
            </select>
            {loadingPackages && <small className="text-primary">Loading available packages...</small>}
          </div>

          <div className="col-md-4 mb-4">
            <label htmlFor="projectImages" className="lables">Project Images *</label>
            <div className="custom-file-container">
              <input
                type="file"
                id="projectImages"
                name="images"
                className="form-control"
                accept="image/*"
                multiple
                onChange={handleChange}
                required
                style={{padding: '8px'}}
              />
            </div>
            {formData.images.length > 0 && (
              <small className="text-success font-weight-bold d-block mt-2">
                <i className="fas fa-check-circle mr-1"></i> {formData.images.length} image(s) selected
              </small>
            )}
          </div>

          <div className="col-12 mb-4">
            <label htmlFor="projectDescription" className="lables">Project Description *</label>
            <textarea
              id="projectDescription"
              name="project_description"
              className="form-control"
              rows="4"
              value={formData.project_description}
              onChange={handleChange}
              placeholder="Describe the project details, features, and scope..."
              required
            />
          </div>
        </div>

        <hr className="my-4" />

        <div className="d-flex justify-content-end">
          <button type="button" className="btn btn-light mr-3" onClick={() => navigate('/projects')}>Cancel</button>
          <button type="submit" className="btn addbtn" disabled={loading} style={{minWidth: '180px'}}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
                Adding...
              </>
            ) : (
              'Save Project'
            )}
          </button>
        </div>
      </form>
    </div >
  );
};

export default AddProject;